import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import EditableCell from './EditableCell'

const statusOptions = ['Awaiting', 'Opened', 'Replied', 'Landed', 'Dead']
const typeOptions = ['Cold Email', 'LinkedIn DM', 'Referral', 'Inbound', 'Other']

function NoteCell({ notes, onSave, theme, canEdit = true }) {
  const [hovered, setHovered] = useState(false)
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(notes || '')

  const handleSave = () => {
    setEditing(false)
    if (val !== notes) onSave(val)
  }

  return (
    <>
      <div
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          onClick={() => { if (canEdit) { setVal(notes || ''); setEditing(true) } }}
          style={{ fontSize: 13, color: theme.textMuted, cursor: canEdit ? 'pointer' : 'default', opacity: notes ? 0.7 : 0.3, lineHeight: 1 }}
          title={canEdit ? (notes ? 'Click to edit note' : 'Click to add a note') : undefined}
        >📝</div>
        {hovered && notes && !editing && (
          <div style={{
            position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
            background: theme.surface, border: `1px solid ${theme.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '10px 14px', borderRadius: 4,
            fontSize: 13, color: theme.text, fontFamily: "'Nunito Sans', sans-serif",
            width: 280, whiteSpace: 'normal', zIndex: 100, pointerEvents: 'none',
          }}>
            <div style={{ fontSize: 10, color: theme.accent, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
            {notes}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={e => { if (e.target === e.currentTarget) handleSave() }}>
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: 24, width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 11, color: theme.accent, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 12 }}>Edit Note</div>
            <textarea
              autoFocus
              value={val}
              onChange={e => setVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') handleSave() }}
              rows={4}
              style={{
                width: '100%', background: theme.surfaceAlt, border: `1px solid ${theme.accent}`,
                color: theme.text, padding: '9px 12px', fontSize: 13,
                fontFamily: "'Nunito Sans', sans-serif", resize: 'vertical',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(false)} style={{
                background: 'transparent', border: `1px solid ${theme.border}`, color: theme.textMuted,
                padding: '7px 16px', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                fontFamily: "'Open Sans', sans-serif", cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleSave} style={{
                background: theme.accent, border: 'none', color: '#0a2342',
                padding: '7px 16px', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: 'pointer',
              }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function getFollowUpColor(dateStr, theme) {
  if (!dateStr) return theme.textMuted
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const followUp = new Date(dateStr + 'T12:00:00')
  const daysUntil = Math.ceil((followUp - today) / (1000 * 60 * 60 * 24))
  if (daysUntil < 0) return '#ef4444'
  if (daysUntil <= 2) return '#f97316'
  if (daysUntil <= 7) return '#d9ad2b'
  return theme.textSecondary
}

export default function Pitches({ session, theme, canEdit = true }) {
  const [pitches, setPitches] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sortField, setSortField] = useState('sent_date')
  const [sortDir, setSortDir] = useState('desc')
  const [form, setForm] = useState({
    company: '', contact: '', type: 'Cold Email',
    sent_date: '', status: 'Awaiting', notes: '', follow_up_date: ''
  })

  const statusColors = {
    'Awaiting': { bg: theme.statusBlueBg,   text: theme.statusBlueText   },
    'Opened':   { bg: theme.statusYellowBg, text: theme.statusYellowText },
    'Replied':  { bg: theme.statusGreenBg,  text: theme.statusGreenText  },
    'Landed':   { bg: theme.statusGreenBg,  text: theme.statusGreenText  },
    'Dead':     { bg: theme.statusGrayBg,   text: theme.statusGrayText   },
  }

  useEffect(() => {
    if (session?.user?.id) fetchPitches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const fetchPitches = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('pitches').select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (!error) setPitches(data)
    setLoading(false)
  }

  const handleSave = async () => {
    if (!form.company) return
    setSaving(true)
    const { error } = await supabase.from('pitches')
      .insert([{ ...form, user_id: session.user.id, sent_date: form.sent_date || null, follow_up_date: form.follow_up_date || null }])
    if (!error) {
      setForm({ company: '', contact: '', type: 'Cold Email', sent_date: '', status: 'Awaiting', notes: '', follow_up_date: '' })
      setShowForm(false)
      fetchPitches()
    }
    setSaving(false)
  }

  const handleUpdate = async (id, field, value) => {
    await supabase.from('pitches').update({ [field]: value || null }).eq('id', id)
    await fetchPitches()
  }

  const handleDelete = async (id) => {
    const { error } = await supabase.from('pitches').delete().eq('id', id).eq('user_id', session.user.id)
    if (!error) fetchPitches()
  }

  const inputStyle = {
    width: '100%', background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
    color: theme.text, padding: '9px 12px', fontSize: 13,
    fontFamily: "'Nunito Sans', sans-serif", boxSizing: 'border-box', outline: 'none',
  }

  const labelStyle = {
    fontSize: 10, color: theme.textSecondary, marginBottom: 5,
    letterSpacing: '0.1em', fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const sortedPitches = [...pitches].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return 1
    if (bVal == null) return -1
    if (sortField === 'sent_date' || sortField === 'follow_up_date') {
      aVal = new Date(aVal); bVal = new Date(bVal)
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal
    }
    aVal = String(aVal).toLowerCase(); bVal = String(bVal).toLowerCase()
    if (aVal < bVal) return sortDir === 'desc' ? 1 : -1
    if (aVal > bVal) return sortDir === 'desc' ? -1 : 1
    return 0
  })

  const activePitches  = pitches.filter(p => p.status !== 'Dead' && p.status !== 'Landed')
  const landedPitches  = pitches.filter(p => p.status === 'Landed')
  const repliedPitches = pitches.filter(p => p.status === 'Replied')
  const winRate = pitches.length > 0 ? Math.round((landedPitches.length / pitches.length) * 100) : 0

  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Sent', value: pitches.length },
          { label: 'Active',     value: activePitches.length },
          { label: 'Replies',    value: repliedPitches.length },
          { label: 'Win Rate',   value: pitches.length > 0 ? `${winRate}%` : '—' },
        ].map((stat, i) => (
          <div key={i} style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '18px 20px' }}>
            <div style={{ fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8 }}>{stat.label}</div>
            <div style={{ fontSize: 26, color: theme.text, fontFamily: "'Cardo', serif" }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif" }}>
          {pitches.length} {pitches.length === 1 ? 'pitch' : 'pitches'}
        </div>
        {canEdit && (
          <button onClick={() => setShowForm(!showForm)} style={{
            background: theme.accent, color: '#0a2342', border: 'none',
            padding: '8px 18px', fontSize: 11, letterSpacing: '0.1em',
            textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: 'pointer',
          }}>{showForm ? 'Cancel' : '+ New Pitch'}</button>
        )}
      </div>

      {/* New pitch form */}
      {showForm && (
        <div style={{ background: theme.surface, border: `1px solid ${theme.accent}44`, padding: '24px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: theme.accent, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 20 }}>New Pitch</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div><div style={labelStyle}>Company *</div>
              <input style={inputStyle} placeholder="e.g. HubSpot" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
            <div><div style={labelStyle}>Contact</div>
              <input style={inputStyle} placeholder="e.g. Sarah K." value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} /></div>
            <div><div style={labelStyle}>Pitch Type</div>
              <select style={inputStyle} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                {typeOptions.map(t => <option key={t}>{t}</option>)}</select></div>
            <div><div style={labelStyle}>Date Sent</div>
              <input type="date" style={inputStyle} value={form.sent_date} onChange={e => setForm({ ...form, sent_date: e.target.value })} /></div>
            <div><div style={labelStyle}>Status</div>
              <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {statusOptions.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><div style={labelStyle}>Notes</div>
              <input style={inputStyle} placeholder="e.g. Spoke at conference" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <div><div style={labelStyle}>Follow-up Date</div>
              <input type="date" style={inputStyle} value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} /></div>
          </div>
          <button onClick={handleSave} disabled={saving || !form.company} style={{
            background: form.company ? theme.accent : theme.border, color: '#0a2342', border: 'none',
            padding: '9px 22px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
            fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: form.company ? 'pointer' : 'not-allowed',
          }}>{saving ? 'Saving...' : 'Save Pitch'}</button>
        </div>
      )}

      {pitches.length > 0 && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.05em', fontStyle: 'italic' }}>
          Click any field to edit it inline
        </div>
      )}

      {/* Pitches table */}
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}` }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 130px 40px',
          padding: '11px 20px', borderBottom: `1px solid ${theme.border}`,
          fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
          textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif",
        }}>
          {[
            { label: 'Company',   field: 'company'        },
            { label: 'Contact',   field: 'contact'        },
            { label: 'Type',      field: 'type'           },
            { label: 'Sent',      field: 'sent_date'      },
            { label: 'Follow-up', field: 'follow_up_date' },
            { label: 'Status',    field: 'status'         },
          ].map(col => (
            <span
              key={col.field}
              onClick={() => handleSort(col.field)}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {col.label}
              {sortField === col.field
                ? <span style={{ opacity: 0.8 }}>{sortDir === 'desc' ? ' ↓' : ' ↑'}</span>
                : <span style={{ opacity: 0.25 }}> ↕</span>}
            </span>
          ))}
          <span></span>
        </div>

        {loading && <div style={{ padding: '32px', textAlign: 'center', color: theme.textMuted, fontSize: 13, fontFamily: "'Nunito Sans', sans-serif" }}>Loading pitches...</div>}

        {!loading && pitches.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", marginBottom: 8 }}>No pitches yet</div>
            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", opacity: 0.6 }}>Click "+ New Pitch" to start tracking your outreach</div>
          </div>
        )}

        {!loading && sortedPitches.map((p, i) => (
          <div key={p.id} style={{
            display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 130px 40px',
            padding: theme.rowPadding, borderBottom: i < sortedPitches.length - 1 ? `1px solid ${theme.border}` : 'none',
            alignItems: 'center',
          }}>
            {/* Company + note icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <EditableCell value={p.company} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'company', v)} />
              <NoteCell notes={p.notes} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'notes', v)} />
            </div>
            <EditableCell value={p.contact} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'contact', v)} />
            <EditableCell value={p.type} theme={theme} options={typeOptions} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'type', v)} />
            <EditableCell value={p.sent_date} theme={theme} type="date" canEdit={canEdit} onSave={v => handleUpdate(p.id, 'sent_date', v)} />
            <EditableCell
              value={p.follow_up_date} theme={theme} type="date"
              followUpColor={getFollowUpColor(p.follow_up_date, theme)}
              canEdit={canEdit}
              onSave={v => handleUpdate(p.id, 'follow_up_date', v)}
            />
            <EditableCell value={p.status} theme={theme} isStatus statusOptions={statusOptions} statusColors={statusColors} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'status', v)} />
            {canEdit && <button onClick={() => handleDelete(p.id)} style={{ background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 16, padding: '0 4px', opacity: 0.5 }} title="Delete pitch">×</button>}
          </div>
        ))}
      </div>
    </div>
  )
}